import React, { useState, useMemo } from 'react';
import { Calculator, Info, ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';
import { calculatorConfig, provinces, companyTypes } from '../../data/mock';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';

const SREDCalculator = () => {
  const [mode, setMode] = useState('quick'); // 'quick' or 'advanced'
  const [showResults, setShowResults] = useState(false);
  const [inputs, setInputs] = useState({
    province: '',
    companyType: 'ccpc',
    salaries: '',
    materials: '',
    contractors: '',
    hasOtherAssistance: false,
    otherAssistance: '',
    fiscalYear: '2025'
  });

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const parseNumber = (value) => {
    const num = parseFloat(value.replace(/[^0-9.-]+/g, ''));
    return isNaN(num) ? 0 : num;
  };

  const results = useMemo(() => {
    const salaries = parseNumber(inputs.salaries);
    const materials = parseNumber(inputs.materials);
    const contractors = parseNumber(inputs.contractors);
    const assistance = inputs.hasOtherAssistance ? parseNumber(inputs.otherAssistance) : 0;

    const rates = calculatorConfig.expenditureRates;
    
    // Calculate maximum estimates
    const maxSalaries = salaries * rates.salaries.maxRate;
    const maxMaterials = materials * rates.materials.maxRate;
    const maxContractors = contractors * rates.contractors.maxRate;
    const maxTotal = Math.max(0, maxSalaries + maxMaterials + maxContractors - assistance);

    // Calculate conservative estimates
    const conservativeSalaries = salaries * rates.salaries.maxRate * rates.salaries.conservativeMultiplier;
    const conservativeMaterials = materials * rates.materials.maxRate * rates.materials.conservativeMultiplier;
    const conservativeContractors = contractors * rates.contractors.maxRate * rates.contractors.conservativeMultiplier;
    const conservativeTotal = Math.max(0, conservativeSalaries + conservativeMaterials + conservativeContractors - assistance);

    // Determine primary insight
    const categories = [
      { name: 'salaries', value: maxSalaries, label: 'team salaries' },
      { name: 'materials', value: maxMaterials, label: 'materials' },
      { name: 'contractors', value: maxContractors, label: 'contractors' }
    ].sort((a, b) => b.value - a.value);

    let insight = '';
    if (categories[0].value > 0) {
      const percentage = Math.round((categories[0].value / (maxSalaries + maxMaterials + maxContractors)) * 100);
      insight = `Most of your potential benefit (${percentage}%) comes from ${categories[0].label}.`;
    }

    return {
      max: {
        salaries: maxSalaries,
        materials: maxMaterials,
        contractors: maxContractors,
        total: maxTotal
      },
      conservative: {
        salaries: conservativeSalaries,
        materials: conservativeMaterials,
        contractors: conservativeContractors,
        total: conservativeTotal
      },
      insight
    };
  }, [inputs]);

  const handleCalculate = () => {
    setShowResults(true);
  };

  const handleInputChange = (field, value) => {
    setInputs(prev => ({ ...prev, [field]: value }));
    setShowResults(false);
  };

  const hasValidInputs = () => {
    return inputs.province && 
           (parseNumber(inputs.salaries) > 0 || 
            parseNumber(inputs.materials) > 0 || 
            parseNumber(inputs.contractors) > 0);
  };

  return (
    <section id="calculator" className="bg-black py-24 lg:py-32 border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mb-12">
          <h2 className="text-4xl md:text-5xl font-semibold text-white mb-6">
            Innovation Refund <span className="text-[#4A90A4]">Calculator</span>
          </h2>
          <p className="text-white/60 text-lg">
            Get a quick estimate of how much innovation funding your business could unlock. Then speak with our team to turn estimates into results.
          </p>
        </div>

        {/* Mode Switcher */}
        <div className="flex mb-8">
          <button
            onClick={() => setMode('quick')}
            className={`px-6 py-3 font-medium transition-all duration-300 ${
              mode === 'quick'
                ? 'bg-[#4A90A4] text-white'
                : 'bg-white/5 text-white/60 hover:text-white'
            }`}
          >
            Quick Estimator
          </button>
          <button
            onClick={() => setMode('advanced')}
            className={`px-6 py-3 font-medium transition-all duration-300 ${
              mode === 'advanced'
                ? 'bg-[#4A90A4] text-white'
                : 'bg-white/5 text-white/60 hover:text-white'
            }`}
          >
            Advanced
          </button>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Input Panel */}
          <div className="bg-white/5 border border-white/10 p-8">
            <div className="flex items-center gap-3 mb-8">
              <Calculator size={24} className="text-[#4A90A4]" />
              <h3 className="text-xl font-semibold text-white">Enter Your Details</h3>
            </div>

            <div className="space-y-6">
              {/* Province */}
              <div className="space-y-2">
                <Label className="text-white/80">Province/Territory *</Label>
                <Select
                  value={inputs.province}
                  onValueChange={(value) => handleInputChange('province', value)}
                >
                  <SelectTrigger className="bg-white/5 border-white/10 text-white h-12">
                    <SelectValue placeholder="Select province" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a1a] border-white/10">
                    {provinces.map((province) => (
                      <SelectItem 
                        key={province.code} 
                        value={province.code}
                        className="text-white hover:bg-[#5BA0B4]/10 focus:bg-white/10"
                      >
                        {province.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Company Type */}
              <div className="space-y-2">
                <Label className="text-white/80">Type of Company *</Label>
                <Select
                  value={inputs.companyType}
                  onValueChange={(value) => handleInputChange('companyType', value)}
                >
                  <SelectTrigger className="bg-white/5 border-white/10 text-white h-12">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a1a] border-white/10">
                    {companyTypes.map((type) => (
                      <SelectItem 
                        key={type.value} 
                        value={type.value}
                        className="text-white hover:bg-[#5BA0B4]/10 focus:bg-white/10"
                      >
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {inputs.companyType === 'not-sure' && (
                  <p className="text-white/40 text-sm mt-2">
                    No worries! We'll use conservative estimates. Our team can help clarify your status.
                  </p>
                )}
              </div>

              {mode === 'advanced' && (
                <div className="space-y-2">
                  <Label className="text-white/80">Fiscal Year End</Label>
                  <Select
                    value={inputs.fiscalYear}
                    onValueChange={(value) => handleInputChange('fiscalYear', value)}
                  >
                    <SelectTrigger className="bg-white/5 border-white/10 text-white h-12">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a1a] border-white/10">
                      {['2023', '2024', '2025'].map((year) => (
                        <SelectItem 
                          key={year} 
                          value={year}
                          className="text-white hover:bg-[#5BA0B4]/10 focus:bg-white/10"
                        >
                          {year}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Expenditure Inputs */}
              <div className="border-t border-white/10 pt-6">
                <h4 className="text-white font-medium mb-4">Annual Innovation Spending</h4>
                
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-white/80 flex items-center gap-2">
                      Innovation Salaries
                      <span className="text-white/40 text-xs">(Team members involved in innovation)</span>
                    </Label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40">$</span>
                      <Input
                        type="text"
                        value={inputs.salaries}
                        onChange={(e) => handleInputChange('salaries', e.target.value)}
                        className="bg-white/5 border-white/10 text-white pl-8 h-12"
                        placeholder="0"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-white/80 flex items-center gap-2">
                      Innovation Materials
                      <span className="text-white/40 text-xs">(Materials consumed in projects)</span>
                    </Label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40">$</span>
                      <Input
                        type="text"
                        value={inputs.materials}
                        onChange={(e) => handleInputChange('materials', e.target.value)}
                        className="bg-white/5 border-white/10 text-white pl-8 h-12"
                        placeholder="0"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-white/80 flex items-center gap-2">
                      Canadian Contractors
                      <span className="text-white/40 text-xs">(Supporting innovation projects)</span>
                    </Label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40">$</span>
                      <Input
                        type="text"
                        value={inputs.contractors}
                        onChange={(e) => handleInputChange('contractors', e.target.value)}
                        className="bg-white/5 border-white/10 text-white pl-8 h-12"
                        placeholder="0"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Other Assistance */}
              <div className="flex items-center justify-between py-4 border-t border-white/10">
                <Label className="text-white/80">Received other government assistance?</Label>
                <Switch
                  checked={inputs.hasOtherAssistance}
                  onCheckedChange={(checked) => handleInputChange('hasOtherAssistance', checked)}
                  className="data-[state=checked]:bg-[#4A90A4]"
                />
              </div>

              {inputs.hasOtherAssistance && (
                <div className="space-y-2">
                  <Label className="text-white/80">Total other assistance received</Label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40">$</span>
                    <Input
                      type="text"
                      value={inputs.otherAssistance}
                      onChange={(e) => handleInputChange('otherAssistance', e.target.value)}
                      className="bg-white/5 border-white/10 text-white pl-8 h-12"
                      placeholder="0"
                    />
                  </div>
                </div>
              )}

              <button
                onClick={handleCalculate}
                disabled={!hasValidInputs()}
                className="w-full bg-[#4A90A4] text-white py-4 font-medium flex items-center justify-center gap-3 hover:bg-[#5BA0B4] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Calculate Estimate
                <Calculator size={18} />
              </button>
            </div>
          </div>

          {/* Results Panel */}
          <div className="bg-white/5 border border-white/10 p-8">
            {!showResults ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-12">
                <div className="w-20 h-20 bg-white/5 flex items-center justify-center mb-6">
                  <Calculator size={40} className="text-white/20" />
                </div>
                <h3 className="text-xl font-semibold text-white/40 mb-3">Your Results Will Appear Here</h3>
                <p className="text-white/30 max-w-sm">
                  Enter your innovation spending details and click calculate to see your estimated refund.
                </p>
              </div>
            ) : (
              <div className="space-y-8">
                <div>
                  <p className="text-white/60 mb-2">Here's a rough estimate of how innovation incentives could support your business:</p>
                  <h3 className="text-lg font-medium text-white">Estimated Annual Refund</h3>
                </div>

                {/* Total Estimate Range */}
                <div className="bg-[#4A90A4]/10 border border-[#4A90A4]/30 p-6">
                  <div className="flex justify-between items-end mb-4">
                    <div>
                      <p className="text-white/60 text-sm mb-1">Conservative Estimate</p>
                      <p className="text-3xl font-bold text-white">{formatCurrency(results.conservative.total)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-white/60 text-sm mb-1">Up to</p>
                      <p className="text-3xl font-bold text-[#4A90A4]">{formatCurrency(results.max.total)}</p>
                    </div>
                  </div>
                  <div className="h-3 bg-white/10 relative overflow-hidden">
                    <div 
                      className="absolute left-0 top-0 h-full bg-[#4A90A4]/50"
                      style={{ width: `${(results.conservative.total / results.max.total) * 100}%` }}
                    />
                    <div 
                      className="absolute left-0 top-0 h-full bg-[#4A90A4]"
                      style={{ width: '100%', opacity: 0.3 }}
                    />
                  </div>
                </div>

                {/* Breakdown */}
                <div className="space-y-4">
                  <h4 className="text-white font-medium">Breakdown by Category</h4>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-3 border-b border-white/10">
                      <span className="text-white/70">Innovation Salaries</span>
                      <span className="text-white font-medium">
                        {formatCurrency(results.conservative.salaries)} - {formatCurrency(results.max.salaries)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-3 border-b border-white/10">
                      <span className="text-white/70">Innovation Materials</span>
                      <span className="text-white font-medium">
                        {formatCurrency(results.conservative.materials)} - {formatCurrency(results.max.materials)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-3 border-b border-white/10">
                      <span className="text-white/70">Canadian Contractors</span>
                      <span className="text-white font-medium">
                        {formatCurrency(results.conservative.contractors)} - {formatCurrency(results.max.contractors)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Insight */}
                {results.insight && (
                  <div className="bg-white/5 p-4 flex items-start gap-3">
                    <Info size={18} className="text-[#4A90A4] mt-0.5 flex-shrink-0" />
                    <p className="text-white/70 text-sm">{results.insight}</p>
                  </div>
                )}

                {/* CTAs */}
                <div className="space-y-3 pt-4">
                  <a
                    href="#contact"
                    className="w-full bg-[#4A90A4] text-white py-4 font-medium flex items-center justify-center gap-3 hover:bg-[#5BA0B4] transition-all duration-300"
                  >
                    Book a Free Innovation Review
                    <ArrowRight size={18} />
                  </a>
                  <button
                    className="w-full bg-white/10 text-white py-4 font-medium flex items-center justify-center gap-3 hover:bg-[#5BA0B4]/20 transition-all duration-300"
                  >
                    Send Results to Our Team
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mt-8 p-6 border border-white/10 bg-white/[0.02]">
          <div className="flex items-start gap-3">
            <Info size={18} className="text-white/40 mt-0.5 flex-shrink-0" />
            <p className="text-white/40 text-sm leading-relaxed">
              {calculatorConfig.disclaimer}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SREDCalculator;
